const WALL_BUILDER_MAX_PANELS = 999;
if (!customElements.get('wall-builder')) {
  customElements.define(
    'wall-builder',
    class WallBuilder extends HTMLElement {
      connectedCallback() {
        this.rows = Array.from(this.querySelectorAll('[data-row]')).map((el) => ({
          compareAt: Number(el.dataset.compareAt) || Number(el.dataset.price) || 0,
          el,
          lineEl: el.querySelector('[data-line-total]'),
          name: (el.querySelector('.wall-builder__name')?.textContent || '').trim(),
          packValue: el.dataset.packValue || '',
          panels: Math.max(1, Number(el.dataset.panels) || 1),
          price: Number(el.dataset.price) || 0,
          qtyEl: el.querySelector('[data-qty]'),
          unitEl: el.querySelector('.wall-builder__unit'),
          variantId: Number(el.dataset.variantId),
        }));

        if (!this.rows.length) return;

        this.totalPanelsEl = this.querySelector('[data-total-panels]');
        this.totalPriceEl = this.querySelector('[data-total-price]');
        this.submitEl =
          this.querySelector('[data-submit]') ||
          document.querySelector('form[data-type="add-to-cart-form"] [type="submit"][name="add"]');

        this.packIndex = Math.max(0, (Number(this.dataset.packPosition) || 1) - 1);
        this.variants = this.readVariants();
        this.resolveRows();

        this.singleRow = this.rows.reduce((a, b) => (a.panels <= b.panels ? a : b));

        this.quantities = new Map(this.rows.map((row) => [row, 0]));
        this.quantities.set(this.singleRow, 1);

        this.setUpZoom();

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('input', this.onQtyInput.bind(this));
        this.addEventListener('change', this.onQtyCommit.bind(this));
        this.addEventListener('keydown', this.onQtyKeydown.bind(this));
        this.addEventListener('focusin', this.onQtyFocus.bind(this));
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeZoom();
        });
        document.addEventListener('venue:variant:updated', () => {
          this.resolveRows();
          this.render();
        });

        ['change', 'click', 'input'].forEach((type) => {
          document.addEventListener(type, (event) => {
            if (!event.target.closest('product-accessories')) return;
            setTimeout(() => this.syncStickyBar(), 0);
          });
        });

        this.render();
      }

      readVariants() {
        const node = this.querySelector('[data-wall-variants]');
        if (!node) return [];

        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          return [];
        }
      }

      selectedOptions() {
        const input = document.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
        const id = input ? Number(input.value) : 0;
        const match = this.variants.find((variant) => variant.id === id);
        return match ? match.options : null;
      }

      resolveRows() {
        if (!this.variants.length) return;

        const selected = this.selectedOptions();

        this.rows.forEach((row) => {
          const candidates = this.variants.filter((variant) => variant.options[this.packIndex] === row.packValue);
          if (!candidates.length) return;

          const exact = selected
            ? candidates.find((variant) =>
              variant.options.every((value, index) => index === this.packIndex || value === selected[index]),
            )
            : null;

          const variant = exact || candidates[0];

          row.variantId = variant.id;
          row.price = variant.price;
          row.compareAt = variant.compareAt || variant.price;
          row.panels = Math.max(1, variant.panels || 1);

          row.el.dataset.variantId = String(variant.id);
          row.el.dataset.price = String(variant.price);
          row.el.dataset.panels = String(row.panels);

          this.renderUnit(row);
        });
      }

      renderUnit(row) {
        if (!row.unitEl) return;

        const perPack = this.dataset.perPackLabel || '';
        const perUnit = this.dataset.perUnitLabel || '';
        const unitPrice = Math.round(row.price / row.panels);

        if (row.panels > 1) {
          row.unitEl.innerHTML = `${this.money(row.price)} ${perPack} <span class="wall-builder__per-unit">(${this.money(unitPrice)} ${perUnit})</span>`;
          return;
        }

        row.unitEl.innerHTML = `${this.money(unitPrice)} ${perUnit}`;
      }

      setUpZoom() {
        this.modal = this.querySelector('[data-zoom-modal]');
        if (!this.modal) return;

        document.body.appendChild(this.modal);

        this.modal.addEventListener('click', (event) => {
          if (event.target.classList.contains('wall-builder-zoom__image')) return;
          this.closeZoom();
        });
      }

      onClick(event) {
        const trigger = event.target.closest('[data-zoom]');
        if (trigger) {
          this.openZoom(trigger);
          return;
        }

        this.onStep(event);
      }

      openZoom(trigger) {
        const source = trigger.dataset.zoomSrc;
        const modal = this.modal;
        const target = modal && modal.querySelector('[data-zoom-target]');
        if (!source || !target) return;

        const thumb = trigger.querySelector('img');
        const image = document.createElement('img');
        image.className = 'wall-builder-zoom__image';
        image.src = source;
        image.alt = thumb ? thumb.alt : '';
        target.replaceChildren(image);

        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        this.zoomTrigger = trigger;

        const closeBtn = modal.querySelector('[data-zoom-close]');
        if (closeBtn) closeBtn.focus();
      }

      closeZoom() {
        const modal = this.modal;
        if (!modal || modal.hidden) return;

        modal.hidden = true;
        document.body.style.removeProperty('overflow');
        document.documentElement.style.removeProperty('overflow');

        if (this.zoomTrigger) {
          this.zoomTrigger.focus();
          this.zoomTrigger = null;
        }
      }

      onQtyFocus(event) {
        if (event.target.matches('[data-qty]')) event.target.select();
      }

      onQtyInput(event) {
        const field = event.target;
        if (!field.matches('[data-qty]')) return;

        const digits = field.value.replace(/[^0-9]/g, '');
        if (digits !== field.value) field.value = digits;
      }

      onQtyKeydown(event) {
        if (!event.target.matches('[data-qty]')) return;
        if (event.key === 'Enter') {
          event.preventDefault();
          event.target.blur();
        }
      }

      onQtyCommit(event) {
        const field = event.target;
        if (!field.matches('[data-qty]')) return;

        const row = this.rows.find((r) => r.qtyEl === field);
        if (!row) return;

        const wanted = Math.max(0, parseInt(field.value, 10) || 0);
        const others = this.totalPanels() - (this.quantities.get(row) || 0) * row.panels;
        const room = Math.floor((WALL_BUILDER_MAX_PANELS - others) / row.panels);

        this.quantities.set(row, Math.max(0, Math.min(wanted, room)));
        this.upgrade();
        this.render();
      }

      onStep(event) {
        const button = event.target.closest('[data-step]');
        if (!button) return;

        const row = this.rows.find((r) => r.el.contains(button));
        if (!row) return;

        const step = Number(button.dataset.step);
        const current = this.quantities.get(row) || 0;

        if (step > 0 && this.totalPanels() + row.panels > WALL_BUILDER_MAX_PANELS) return;

        this.quantities.set(row, Math.max(0, current + step));
        this.upgrade();
        this.render();
      }

      totalPanels() {
        return this.rows.reduce((sum, row) => sum + (this.quantities.get(row) || 0) * row.panels, 0);
      }

      upgrade() {
        const packs = [...this.rows].sort((a, b) => a.panels - b.panels);
        let changed = true;

        while (changed) {
          changed = false;

          for (let b = packs.length - 1; b > 0; b -= 1) {
            const bigger = packs[b];

            for (let sm = 0; sm < b; sm += 1) {
              const smaller = packs[sm];
              if (bigger.panels <= smaller.panels) continue;
              if (bigger.panels % smaller.panels !== 0) continue;

              const needed = bigger.panels / smaller.panels;
              if (needed < 2 || needed * smaller.price <= bigger.price) continue;

              while ((this.quantities.get(smaller) || 0) >= needed) {
                this.quantities.set(smaller, this.quantities.get(smaller) - needed);
                this.quantities.set(bigger, (this.quantities.get(bigger) || 0) + 1);
                changed = true;
              }
            }
          }
        }
      }

      render() {
        const panels = this.totalPanels();
        let total = 0;
        let compareAt = 0;

        this.rows.forEach((row) => {
          const qty = this.quantities.get(row) || 0;
          const line = qty * row.price;
          total += line;
          compareAt += qty * row.compareAt;

          row.qtyEl.value = String(qty);
          row.lineEl.innerHTML = this.money(line);
          row.el.classList.toggle('wall-builder__row--empty', qty === 0);
        });

        if (this.totalPanelsEl) this.totalPanelsEl.textContent = String(panels);
        if (this.totalPriceEl) this.totalPriceEl.innerHTML = this.money(total);

        if (this.submitEl) {
          this.submitEl.disabled = panels === 0;
          this.submitEl.setAttribute('aria-disabled', panels === 0);
        }

        this.dataset.total = total;
        this.dataset.compareAt = compareAt;

        this.syncStickyBar();

        this.dispatchEvent(
          new CustomEvent('wall-builder:changed', {
            bubbles: true,
            detail: { compareAt, items: this.getItems(), panels, total },
          }),
        );
      }

      syncStickyBar() {
        const label = document.querySelector('.stickyCart .stickyOptions .form__label');
        const panelsEl = this.querySelector('.wall-builder__panels');
        if (!label || !panelsEl) return;

        if (label.dataset.variantTitle === undefined) label.dataset.variantTitle = label.textContent;

        const packValues = this.rows.map((row) => row.name);
        const parts = label.dataset.variantTitle
          .split('/')
          .map((part) => part.trim())
          .filter((part) => part.length && !packValues.includes(part));

        parts.push(panelsEl.textContent.replace(/\s+/g, ' ').trim());

        const addonsEl = document.querySelector('.accessories-total__count');
        const addonsCount = Number(document.querySelector('[data-accessories-count]')?.textContent) || 0;
        if (addonsEl && addonsCount > 0) parts.push(addonsEl.textContent.replace(/\s+/g, ' ').trim());

        label.textContent = parts.join(' / ');
      }

      getItems() {
        return this.rows
          .map((row) => ({ id: row.variantId, quantity: this.quantities.get(row) || 0 }))
          .filter((item) => item.quantity > 0);
      }

      money(cents) {
        const format = this.dataset.moneyFormat || (window.theme && window.theme.money_format);

        if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && format) {
          return window.Shopify.formatMoney(cents, format);
        }

        return (cents / 100).toFixed(2);
      }
    },
  );
}
